import {
  difference,
  filter,
  find,
  flatMap,
  includes,
  isEmpty,
  map,
  omit,
  some,
  sortBy,
  uniq,
  uniqBy,
} from 'es-toolkit/compat';

import { Orchestration } from 'api/experimentResults';
import { MultiplexPanelSlideView, MultiplexPanelsResponse } from 'interfaces/multiplexPanels';
import { isWorkflowPostProcessingResult, WorkflowExportKind } from './workflowExportSource';

export const ALL_STUDY_SLIDES_KEY = 'all-study-slides';
export const NOT_IN_A_PANEL_KEY = 'not-in-a-panel';

export type WorkflowResultStatus = 'published' | 'approved' | 'notApproved';

export interface SlideRunResult {
  runId: string;
  createdAt?: string;
  isPublished: boolean;
  isInternallyApproved: boolean;
  status: WorkflowResultStatus;
}

export interface SelectionSlide {
  slideId: string;
  caseName: string;
  slideName: string;
  label: string;
  // The runs that produced a final result for this slide, newest first.
  results: SlideRunResult[];
}

export interface SelectionPanel {
  key: string;
  name: string;
  kind: 'panel' | 'allStudySlides' | 'unassigned';
  slides: SelectionSlide[];
}

export type ResultsInventory = 'known' | 'unavailable';

export const RESULTS_UNAVAILABLE_REASON = 'Post-processing runs could not be loaded, so this result is not known.';

export interface SelectionModel {
  panels: SelectionPanel[];
  // False for users who may not view unpublished results: no per-slide result data exists, so every slide follows
  // the published results.
  resultsKnown: boolean;
  // 'unavailable' is a failed orchestration request without a cached list. Empty runs are then not a successful
  // empty inventory.
  resultsInventory: ResultsInventory;
  // Study flags can establish published results when the run list is unavailable. A successful empty list does not.
  publishedExportEligible: boolean;
  // Every run carrying results of the dialog's kind, newest first.
  runIds: string[];
}

// One results choice. A slide follows Published or Approved as a source, or a particular run.
export type ResultsChoice =
  | { source: 'published' }
  | { source: 'approved' }
  | { source: 'latest' }
  | { source: 'run'; runIds: string[] };

export interface ResultPick {
  key: string;
  source: 'published' | 'approved' | 'run';
  runId?: string;
}

export interface SelectionState {
  // Level 2: Results for this panel, by panel key. Absent means the same as all slides.
  panelChoices: Record<string, ResultsChoice>;
  // Orchestration ids chosen for one panel. Absent means that panel's newest covering run when its effective
  // source is a specific run. An empty list is an explicit Clear and stays empty.
  panelRunIds: Record<string, string[]>;
  // Level 3: the results chosen for one slide, by slide id. An empty list is a deliberate choice of no result.
  slideChoices: Record<string, string[]>;
  // Slides the user unchecked. Everything else with a result is selected, including slides that load later.
  excludedSlideIds: string[];
}

export const emptySelectionState: SelectionState = {
  panelChoices: {},
  panelRunIds: {},
  slideChoices: {},
  excludedSlideIds: [],
};

// Panel keys and run ids contain neither separator. An empty id list is `panelKey:` so Clear survives a reload.
const PANEL_RUN_SEPARATOR = ';';
const PANEL_KEY_SEPARATOR = ':';

export const encodePanelRunSelections = (selections: Record<string, string[]>): string | undefined => {
  const parts = Object.keys(selections)
    .sort()
    .map((key) => `${key}${PANEL_KEY_SEPARATOR}${selections[key].join(',')}`);
  return parts.length ? parts.join(PANEL_RUN_SEPARATOR) : undefined;
};

export const parsePanelRunSelections = (value?: string | null): Record<string, string[]> => {
  if (!value) return {};
  const selections: Record<string, string[]> = {};
  value.split(PANEL_RUN_SEPARATOR).forEach((part) => {
    const separator = part.indexOf(PANEL_KEY_SEPARATOR);
    if (separator <= 0) return;
    const key = part.slice(0, separator);
    selections[key] = part
      .slice(separator + 1)
      .split(',')
      .filter(Boolean);
  });
  return selections;
};

const publishedPick: ResultPick = { key: 'published', source: 'published' };
const approvedPick: ResultPick = { key: 'approved', source: 'approved' };
export const getRunPick = (runId: string): ResultPick => ({ key: `run:${runId}`, source: 'run', runId });

const getStatus = (isPublished: boolean, isInternallyApproved: boolean): WorkflowResultStatus =>
  isPublished ? 'published' : isInternallyApproved ? 'approved' : 'notApproved';

// Cell tables use the same post-processing results as features. A normalization run is not one of them.
const isOfKind = (_kind: WorkflowExportKind, flowClassName?: string) => isWorkflowPostProcessingResult(flowClassName);

const getResultsBySlide = (runs: Orchestration[], kind: WorkflowExportKind) => {
  const results = new Map<string, SlideRunResult[]>();
  const names = new Map<string, string>();
  runs.forEach((run) => {
    run.slidesData.forEach((slideData) => {
      if (!isOfKind(kind, slideData.flowClassName)) return;
      const list = results.get(slideData.slideId) ?? [];
      if (find(list, { runId: run.orchestrationId })) return;
      list.push({
        runId: run.orchestrationId,
        createdAt: slideData.createdAt ?? run.createdAtMinimum,
        isPublished: Boolean(slideData.approved),
        isInternallyApproved: Boolean(slideData.internallyApproved),
        status: getStatus(Boolean(slideData.approved), Boolean(slideData.internallyApproved)),
      });
      results.set(slideData.slideId, list);
      if (slideData.caseName && !names.has(slideData.slideId)) names.set(slideData.slideId, slideData.caseName);
    });
  });
  results.forEach((list, slideId) =>
    results.set(slideId, sortBy(list, [(result) => -new Date(result.createdAt ?? 0).getTime(), 'runId']))
  );
  return { results, names };
};

const toSelectionSlide = (
  slideId: string,
  details: { caseName?: string; slideName?: string },
  results: SlideRunResult[]
): SelectionSlide => {
  const caseName = details.caseName || 'No case';
  const slideName = details.slideName || slideId;
  // The case label is often copied from the source file name. A slash then reads as two identical slides.
  const label = caseName === slideName ? slideName : `${caseName} / ${slideName}`;
  return { slideId, caseName, slideName, label, results };
};

const describePanelSlide = (slide: MultiplexPanelSlideView) => ({
  caseName: (slide.caseLabels ?? []).join(', '),
  slideName: slide.originalFileName ?? undefined,
});

// Panels are the multiplex panels the Cell typing & marker positivity step uses, as the panels response lists them.
// Slides outside every panel stay visible in Not in a panel, and a study without panels has one effective panel.
export const buildSelectionModel = ({
  panels,
  runs,
  kind,
  resultsKnown,
  resultsInventory = 'known',
  publishedExportEligible = false,
}: {
  panels?: MultiplexPanelsResponse;
  runs: Orchestration[];
  kind: WorkflowExportKind;
  resultsKnown: boolean;
  resultsInventory?: ResultsInventory;
  publishedExportEligible?: boolean;
}): SelectionModel => {
  const { results, names } = getResultsBySlide(runs, kind);
  const resultsOf = (slideId: string) => results.get(slideId) ?? [];
  const definedPanels = panels?.panels ?? [];
  const panelSlideIds = new Set(flatMap(definedPanels, (panel) => map(panel.slides, 'slideId')));

  const unassignedSlides = new Map<string, { caseName?: string; slideName?: string }>();
  flatMap(panels?.unassignedGroups ?? [], (group) => group.slides).forEach((slide) => {
    if (!panelSlideIds.has(slide.slideId)) unassignedSlides.set(slide.slideId, describePanelSlide(slide));
  });
  // A slide with a result that no response lists is still shown, so no result is hidden.
  results.forEach((_list, slideId) => {
    if (!panelSlideIds.has(slideId) && !unassignedSlides.has(slideId)) {
      unassignedSlides.set(slideId, { caseName: names.get(slideId) });
    }
  });
  const unassigned = map([...unassignedSlides.entries()], ([slideId, details]) =>
    toSelectionSlide(
      slideId,
      { caseName: details.caseName || names.get(slideId), slideName: details.slideName },
      resultsOf(slideId)
    )
  );

  const selectionPanels: SelectionPanel[] = map(definedPanels, (panel) => ({
    key: panel.panelId,
    name: panel.name?.trim() || 'Multiplex panel',
    kind: 'panel' as const,
    slides: map(panel.slides, (slide) =>
      toSelectionSlide(
        slide.slideId,
        {
          caseName: describePanelSlide(slide).caseName || names.get(slide.slideId),
          slideName: slide.originalFileName ?? undefined,
        },
        resultsOf(slide.slideId)
      )
    ),
  }));

  if (isEmpty(definedPanels)) {
    selectionPanels.push({
      key: ALL_STUDY_SLIDES_KEY,
      name: 'All study slides',
      kind: 'allStudySlides',
      slides: unassigned,
    });
  } else if (!isEmpty(unassigned)) {
    selectionPanels.push({ key: NOT_IN_A_PANEL_KEY, name: 'Not in a panel', kind: 'unassigned', slides: unassigned });
  }

  const runIds = map(
    sortBy(
      filter(runs, (run) => some(run.slidesData, (slideData) => isOfKind(kind, slideData.flowClassName))),
      [(run) => -new Date(run.createdAtMinimum ?? 0).getTime(), 'orchestrationId']
    ),
    'orchestrationId'
  );
  return { panels: selectionPanels, resultsKnown, resultsInventory, publishedExportEligible, runIds };
};

export const getModelSlides = (model: SelectionModel) =>
  uniqBy(
    flatMap(model.panels, (panel) => panel.slides),
    'slideId'
  );

export interface PreferenceAvailability {
  published: boolean;
  approved: boolean;
  runs: boolean;
}

export const getPreferenceAvailability = (model: SelectionModel): PreferenceAvailability => {
  const slides = getModelSlides(model);
  return {
    published: !model.resultsKnown || some(slides, (slide) => some(slide.results, (result) => result.isPublished)),
    approved:
      model.resultsKnown && some(slides, (slide) => some(slide.results, (result) => result.isInternallyApproved)),
    runs: !isEmpty(model.runIds),
  };
};

export const getChoiceNoResultReason = (choice: ResultsChoice, slide: SelectionSlide) => {
  if (isEmpty(slide.results)) return 'No post-processing result exists for this slide.';
  if (choice.source === 'published') return 'No published result for this slide. Choose another result to include it.';
  if (choice.source === 'approved') return 'No approved result for this slide. Choose another result to include it.';
  if (choice.source === 'run' && isEmpty(choice.runIds))
    return 'No run is chosen. Choose a result to include this slide.';
  return 'None of the chosen runs produced a result for this slide. Choose another result to include it.';
};

export const getSlidePicks = (model: SelectionModel, slide: SelectionSlide, choice: ResultsChoice): ResultPick[] => {
  if (!model.resultsKnown) return [publishedPick];
  // A failed list is not proof that published results are absent. The export mode does not need run ids.
  if (model.resultsInventory === 'unavailable') {
    return choice.source === 'published' && model.publishedExportEligible ? [publishedPick] : [];
  }
  switch (choice.source) {
    case 'published':
      return some(slide.results, (result) => result.isPublished) ? [publishedPick] : [];
    case 'approved':
      return some(slide.results, (result) => result.isInternallyApproved) ? [approvedPick] : [];
    case 'latest':
      return isEmpty(slide.results) ? [] : [getRunPick(slide.results[0].runId)];
    default:
      return map(
        filter(slide.results, (result) => includes(choice.runIds, result.runId)),
        (result) => getRunPick(result.runId)
      );
  }
};

export interface ResolvedSlide {
  slide: SelectionSlide;
  picks: ResultPick[];
  // The choice that applies: the slide's own, else the panel's, else the preference.
  choice: ResultsChoice;
  hasOwnChoice: boolean;
  hasResult: boolean;
  isSelected: boolean;
  // Why a slide has no result to export; set only when it has none.
  reason?: string;
  // The other panels listing the same slide, which is a data discrepancy shown rather than merged.
  alsoInPanelNames: string[];
}

export interface ResolvedPanel {
  panel: SelectionPanel;
  panelChoice?: ResultsChoice;
  slides: ResolvedSlide[];
  selectableCount: number;
  selectedCount: number;
  // Set when this panel chooses specific orchestrations, including when that choice is inherited.
  showsRunSelector: boolean;
  effectiveRunIds: string[];
  ignoredRunCount: number;
}

export interface ResolvedSelection {
  panels: ResolvedPanel[];
  selectableSlideCount: number;
  selectedSlideCount: number;
  // Slides whose results differ from what the preference alone would give them.
  differingSlideCount: number;
  // Panel keys in the open dialog's run store that are not panels of this study.
  unknownPanelCount: number;
}

const toSlideChoice = (slideChoice: string[]): ResultsChoice => ({ source: 'run', runIds: slideChoice });

const sameKeys = (left: ResultPick[], right: ResultPick[]) =>
  isEmpty(difference(map(left, 'key'), map(right, 'key'))) && isEmpty(difference(map(right, 'key'), map(left, 'key')));

export const resolveSelection = (
  model: SelectionModel,
  preference: ResultsChoice,
  state: SelectionState
): ResolvedSelection => {
  const panelNamesBySlide = new Map<string, string[]>();
  model.panels.forEach((panel) =>
    panel.slides.forEach((slide) =>
      panelNamesBySlide.set(slide.slideId, [...(panelNamesBySlide.get(slide.slideId) ?? []), panel.name])
    )
  );
  const excluded = new Set(state.excludedSlideIds);
  const differing = new Set<string>();
  const knownPanelKeys = new Set(map(model.panels, 'key'));

  const panels = map(model.panels, (panel): ResolvedPanel => {
    const panelChoice = state.panelChoices[panel.key];
    const panelSource = panelChoice?.source ?? preference.source;
    const showsRunSelector = panelSource === 'run';
    const storedRunIds = state.panelRunIds[panel.key];
    const effectiveRunIds = showsRunSelector ? getEffectivePanelRunIds(model, panel.key, storedRunIds) : [];
    const covering = new Set(getPanelRunIds(model, panel.key));
    const ignoredRunCount = filter(storedRunIds ?? [], (runId) => !covering.has(runId)).length;
    const panelRunChoice: ResultsChoice = { source: 'run', runIds: effectiveRunIds };
    // A specific-run preference means each panel's own newest covering run, not one study-wide list.
    const baselineChoice: ResultsChoice =
      preference.source === 'run' ? { source: 'run', runIds: getEffectivePanelRunIds(model, panel.key) } : preference;
    const slides = map(panel.slides, (slide): ResolvedSlide => {
      const ownChoice = state.slideChoices[slide.slideId];
      const resolvedChoice: ResultsChoice =
        ownChoice !== undefined
          ? toSlideChoice(ownChoice)
          : showsRunSelector
          ? panelRunChoice
          : panelChoice ?? preference;
      const picks = getSlidePicks(model, slide, resolvedChoice);
      if (!sameKeys(picks, getSlidePicks(model, slide, baselineChoice))) differing.add(slide.slideId);
      const hasResult = !isEmpty(picks);
      return {
        slide,
        picks,
        choice: resolvedChoice,
        hasOwnChoice: ownChoice !== undefined,
        hasResult,
        isSelected: hasResult && !excluded.has(slide.slideId),
        reason: hasResult
          ? undefined
          : model.resultsInventory === 'unavailable'
          ? RESULTS_UNAVAILABLE_REASON
          : getChoiceNoResultReason(resolvedChoice, slide),
        alsoInPanelNames: difference(panelNamesBySlide.get(slide.slideId) ?? [], [panel.name]),
      };
    });
    return {
      panel,
      panelChoice,
      slides,
      selectableCount: filter(slides, 'hasResult').length,
      selectedCount: filter(slides, 'isSelected').length,
      showsRunSelector,
      effectiveRunIds,
      ignoredRunCount,
    };
  });

  // Keep every panel row. A shared slide can be cleared in the first panel and still exported from a later one.
  const resolvedRows = flatMap(panels, (panel) => panel.slides);
  const distinctSlideCount = (matches: (resolved: ResolvedSlide) => boolean) =>
    new Set(map(filter(resolvedRows, matches), (resolved) => resolved.slide.slideId)).size;
  return {
    panels,
    selectableSlideCount: distinctSlideCount((resolved) => resolved.hasResult),
    selectedSlideCount: distinctSlideCount((resolved) => resolved.isSelected),
    differingSlideCount: differing.size,
    unknownPanelCount: filter(Object.keys(state.panelRunIds), (panelKey) => !knownPanelKeys.has(panelKey)).length,
  };
};

const getPanelSlideIds = (model: SelectionModel, panelKey: string) =>
  map(find(model.panels, { key: panelKey })?.slides ?? [], 'slideId');

// Choosing Results for all slides replaces every panel and slide choice and re-selects the slides that have a result.
export const applyPreference = (): SelectionState => emptySelectionState;

// Choosing Results for this panel replaces that panel's slides only. Undefined returns to Same as all slides.
export const applyPanelChoice = (
  model: SelectionModel,
  state: SelectionState,
  panelKey: string,
  choice?: ResultsChoice
): SelectionState => {
  const slideIds = getPanelSlideIds(model, panelKey);
  const panelRunIds = { ...state.panelRunIds };
  if (choice?.source === 'run' && choice.runIds) panelRunIds[panelKey] = choice.runIds;
  else delete panelRunIds[panelKey];
  return {
    panelChoices: choice ? { ...state.panelChoices, [panelKey]: choice } : omit(state.panelChoices, panelKey),
    panelRunIds,
    slideChoices: omit(state.slideChoices, slideIds),
    excludedSlideIds: difference(state.excludedSlideIds, slideIds),
  };
};

// Choosing runs for one panel replaces that panel's slide choices and re-selects its slides. It does not change
// another panel. An empty list is Clear and is not filled back in with the newest run.
export const setPanelRunIds = (
  model: SelectionModel,
  state: SelectionState,
  panelKey: string,
  runIds: string[]
): SelectionState => {
  const slideIds = getPanelSlideIds(model, panelKey);
  return {
    ...state,
    panelRunIds: { ...state.panelRunIds, [panelKey]: runIds },
    slideChoices: omit(state.slideChoices, slideIds),
    excludedSlideIds: difference(state.excludedSlideIds, slideIds),
  };
};

// Choosing results for one slide replaces that slide only. Undefined returns the slide to its panel's choice.
export const applySlideResults = (state: SelectionState, slideId: string, runIds?: string[]): SelectionState => ({
  ...state,
  slideChoices: runIds ? { ...state.slideChoices, [slideId]: runIds } : omit(state.slideChoices, slideId),
  excludedSlideIds: difference(state.excludedSlideIds, [slideId]),
});

export const setSlidesSelected = (state: SelectionState, slideIds: string[], isSelected: boolean): SelectionState => ({
  ...state,
  excludedSlideIds: isSelected
    ? difference(state.excludedSlideIds, slideIds)
    : uniq([...state.excludedSlideIds, ...slideIds]),
});

// The panel and slide choices that remain, cleared without touching which slides are selected.
export const resetOverrides = (state: SelectionState): SelectionState => ({
  ...state,
  panelChoices: {},
  panelRunIds: {},
  slideChoices: {},
});

// The runs that produced a result for at least one slide of the panel, newest first. Never a run covering none.
export const getPanelRunIds = (model: SelectionModel, panelKey: string) => {
  const covered = new Set(
    flatMap(find(model.panels, { key: panelKey })?.slides ?? [], (slide) => map(slide.results, 'runId'))
  );
  return filter(model.runIds, (runId) => covered.has(runId));
};

// Absent stored ids mean the panel's newest covering run. An empty list stays empty. Ids that cover none of the
// panel are omitted here and counted as ignored by the caller.
export const getEffectivePanelRunIds = (model: SelectionModel, panelKey: string, stored?: string[]) => {
  const covering = getPanelRunIds(model, panelKey);
  if (!stored) return covering.slice(0, 1);
  return filter(covering, (runId) => includes(stored, runId));
};

// Where a source's results are read: Published and Approved are one source each, every run is its own.
export const getUniquePicks = (selection: ResolvedSelection) =>
  uniqBy(
    flatMap(selection.panels, (panel) => flatMap(panel.slides, (resolved) => resolved.picks)),
    'key'
  );

export interface FeatureRequestGroup {
  key: string;
  panelKey: string;
  panelName: string;
  pick: ResultPick;
  slideIds: string[];
}

// One request per panel and results source, holding only the selected slides assigned to that source.
export const getFeatureRequestGroups = (selection: ResolvedSelection): FeatureRequestGroup[] =>
  flatMap(selection.panels, ({ panel, slides }) => {
    const groups = new Map<string, FeatureRequestGroup>();
    slides.forEach((resolved) => {
      if (!resolved.isSelected) return;
      resolved.picks.forEach((pick) => {
        const key = `${panel.key}|${pick.key}`;
        const group = groups.get(key) ?? { key, panelKey: panel.key, panelName: panel.name, pick, slideIds: [] };
        group.slideIds.push(resolved.slide.slideId);
        groups.set(key, group);
      });
    });
    return [...groups.values()];
  });

export const slideMatchesSearch = (
  resolved: ResolvedSlide,
  panelName: string,
  query: string,
  getRunLabel: (runId: string) => string
) => {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  // Search the run the row displays, including the run a Published or Approved choice stands for.
  const displayedRunIds = map(
    filter(resolved.slide.results, (result) =>
      some(
        resolved.picks,
        (pick) =>
          (pick.source === 'run' && pick.runId === result.runId) ||
          (pick.source === 'published' && result.isPublished) ||
          (pick.source === 'approved' && result.isInternallyApproved)
      )
    ),
    'runId'
  );
  const runLabels = map(displayedRunIds, getRunLabel);
  return `${resolved.slide.label} ${resolved.slide.caseName} ${resolved.slide.slideName} ${
    resolved.slide.slideId
  } ${panelName} ${runLabels.join(' ')}`
    .toLowerCase()
    .includes(term);
};
